<template>
    <div class="instructors">
        <table-control :paging="paging" :search="true" v-model="search"></table-control>

        <el-table class="table-responsive table-flush"
                :show-header="false"
                :data="filteredInstructors">

            <el-table-column width="72">
                <template v-slot="{row}">
                    <span class="avatar rounded-circle">
                        <img :src="row.avatar">
                    </span>
                </template>
            </el-table-column>

            <el-table-column>
                <template v-slot="{row}">
                    <div>
                        <h4 class="mb-0">
                            {{ row.name }}
                        </h4>
                        <small v-if="row.owner">
                            {{ row.owner.name }}
                        </small>
                        <small v-if="row.entity">
                            / {{ row.entity.name }}
                        </small>
                    </div>
                </template>
            </el-table-column>

            <el-table-column align="center" width="100">
                <template v-slot="{row}">

                    <badge type="primary" v-if="row.pivot.referent">
                        {{ $t('dashboard.referent') }}
                    </badge>

                </template>
            </el-table-column>

            <el-table-column align="right" width="50" v-if="canModifyCohort">
                <template v-slot="{row}">

                    <a href="#" class="table-action text-danger" v-if="row.id != $auth.user.id"
                            @click.prevent="remove(row.id)">
                        <i class="tim-icons icon-simple-remove"></i>
                    </a>

                </template>
            </el-table-column>

            <el-table-column align="right" width="50" class-name="actions" v-if="canModifyCohort">
                <template v-slot="{row}">
                    <div class="table-actions">
                        <actions-dropdown :actions="actions(row)"></actions-dropdown>
                    </div>
                </template>
            </el-table-column>
            
        </el-table>


        <div class="card-footer text-center" v-if="canModifyCohort">
            <base-button type="secondary" size="sm" @click="selectMembers">
                <span class="btn-inner--icon"><i class="tim-icons icon-e-add"></i></span>
                <span class="btn-inner--text">{{ $t('dashboard.add-instructors') }}</span>
            </base-button>
        </div>

        <portal to="modals">

            <form-modal v-model="modals.select"
                :title="$t('common.instructors')"
                backdrop='true'
                :centered="false"
            >
                <instructors-select
                    :cohort="cohort"
                    :instructors="currentInstructors"
                    :modal="modals.select"
                    @change="changed"
                ></instructors-select>
            </form-modal>

            <confirm-modal v-model="modals.confirmDelete">
                <p>{{ $t('dashboard.remove-instructor-confirm') }}</p>
            </confirm-modal>

        </portal>
    </div>
</template>

<script>
    import ModalsController from '@trax/classes/ModalsController'
    import InstructorsSelect from './InstructorsSelect'

    export default {

        components: {
            InstructorsSelect
        },

        props: {

            cohort: {
                type: Object,
                required: true
            },

            instructors: {
                type: Array,
            }
        },

        data() {
            return {
                modals: new ModalsController(['select', 'confirmDelete']),
                paging: {
                    currentPage: 1,
                    perPage: 1000,
                    total: 0
                },
                search: '',
                loadedInstructors: null,
            }
        },

        created() {
            this.modals.init(this, 'modals')
        },

        computed: {

            currentInstructors() {
                if (this.loadedInstructors) {
                    return this.loadedInstructors
                }
                return this.instructors
            },

            filteredInstructors() {
                // Search filter.
                let members = this.currentInstructors.filter(member => !this.search
                    || member.name.toLowerCase().includes(this.search.toLowerCase())
                )
                // Sorting.
                members = members.sort((a, b) => a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1)
                this.paging.total = members.length
                return members.slice((this.paging.currentPage - 1) * this.paging.perPage, this.paging.currentPage * this.paging.perPage)
            },

            canModifyCohort() {
                return this.cohort.status != 'archived' && this.$permissions.hasRight('dashboard.learners.write')
            }
        },

        methods: {

            actions(instructor) {
                if (!instructor.pivot.referent) {
                    return [{
                        label: this.$t('dashboard.make-referent'),
                        handler: this.makeReferent,
                        data: instructor.id
                    }]
                } else {
                    return [{
                        label: this.$t('dashboard.remove-referent'),
                        handler: this.removeReferent,
                        data: instructor.id
                    }]
                }
            },

            remove(id) {
                this.modals.open('confirmDelete').then(resp => {
                    if (resp.confirmed) {
                        axios.delete('/astral/api/front/cohorts/'+this.cohort.id+'/members/'+id)
                        .then(resp => {
                            this.changed()
                        })
                        .catch(err => {
                            this.$auth.handleErrorVm(err, this)
                        })        
                    }
                })
            },

            makeReferent(id) {
                axios.post('/astral/api/front/cohorts/'+this.cohort.id+'/members/'+id, {
                    role: 'instructor',
                    referent: true,
                }).then(resp => {
                    this.changed()
                })
                .catch(err => {
                    this.$auth.handleErrorVm(err, this)
                })        
            },

            removeReferent(id) {
                axios.post('/astral/api/front/cohorts/'+this.cohort.id+'/members/'+id, {
                    role: 'instructor',
                    referent: false
                }).then(resp => {
                    this.changed()
                })
                .catch(err => {
                    this.$auth.handleErrorVm(err, this)
                })        
            },

            changed() {
                this.$emit('change')
            },

            selectMembers() {
                this.modals.open('select')
            },
        }
    }
</script>

<style scoped>

    .instructors >>> td {
        padding-right: 0;
    }
    .instructors >>> td.actions {
        padding-left: 0;
        padding-right: 1rem;
    }
    .instructors >>> .table-control form {
        width: 100%;
    }

</style>
